// The Town: a street of neighbors, each with a job of the day.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { drawSprite } from '@avatar/render';
import { NEIGHBORS, jobsForToday, type TodayJob } from '../town';
import { getSave, subscribe } from '../state';
import { GAME_BY_ID, lockInfo } from '../registry';
import { topBar } from '../ui';
import { go } from '../router';
import { activePet, drawPet } from '../pets';
import { drawArt, onArtReady } from '@kit/assets';

export function townScreen(root: HTMLElement) {
  const bar = topBar('Town', { back: 'hub' });
  const stage = h('div', { style: { padding: '0 12px' } });
  const intro = h('p.muted.small.center', { style: { padding: '4px 16px' } }, 'Neighbors need a hand. Jobs pay extra gems, and new jobs arrive every morning.');
  const list = h('div.stack', { style: { padding: '0 16px 24px' } });
  root.append(bar, stage, intro, list);

  let jobs: TodayJob[] = [];
  const S = makeSurface(stage, { aspect: 2 / 3, draw: false, onResize: draw });

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      const n = NEIGHBORS.length;
      const i = Math.floor(p.x / (S.w / n));
      const j = jobs[i];
      if (j) startJob(j);
    },
  });

  function startJob(j: TodayJob) {
    if (j.locked || j.done) return;
    go(`play/${j.job.gameId}/job/${encodeURIComponent(j.key)}`);
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    const s = getSave();
    const painted = drawArt(c, 'scene.town', 0, 0, W, H);
    if (!painted) {
      c.fillStyle = '#bfe3f5'; c.fillRect(0, 0, W, H);
      c.fillStyle = '#9ad67e'; c.fillRect(0, H * 0.62, W, H * 0.38);
      c.fillStyle = '#e4cfa0'; c.fillRect(0, H * 0.78, W, H * 0.12);
      c.fillStyle = '#f6f3ec'; for (let x = 10; x < W; x += 36) c.fillRect(x, H * 0.835, 18, 3);
    }
    const n = NEIGHBORS.length, slot = W / n;
    jobs.forEach((j, i) => {
      const nb = j.neighbor;
      const hw = slot * 0.72, hh = H * 0.3, hx = i * slot + (slot - hw) / 2, hy = H * 0.34;
      if (!painted) {
        c.globalAlpha = j.locked ? 0.45 : 1;
        c.fillStyle = nb.color; c.fillRect(hx, hy, hw, hh);
        c.fillStyle = nb.roof; c.beginPath(); c.moveTo(hx - hw * 0.12, hy); c.lineTo(hx + hw / 2, hy - hh * 0.6); c.lineTo(hx + hw * 1.12, hy); c.closePath(); c.fill();
        c.fillStyle = '#8a5a3b'; roundRect(c, hx + hw * 0.36, hy + hh * 0.45, hw * 0.28, hh * 0.55, 3); c.fill();
        c.fillStyle = '#bfe8ff'; c.fillRect(hx + hw * 0.1, hy + hh * 0.18, hw * 0.2, hh * 0.25); c.fillRect(hx + hw * 0.7, hy + hh * 0.18, hw * 0.2, hh * 0.25);
        c.globalAlpha = 1;
      } else if (j.locked) {
        c.fillStyle = 'rgba(247,243,234,.55)'; c.fillRect(hx - hw * 0.15, hy - hh * 0.65, hw * 1.3, hh * 1.7);
      }
      // marker
      if (!j.locked) {
        c.fillStyle = j.done ? '#5fbf6a' : '#f5c542';
        c.beginPath(); c.arc(hx + hw / 2, hy - hh * 0.85, Math.max(9, slot * 0.1), 0, 7); c.fill();
        c.fillStyle = '#2a2a2e'; c.font = `900 ${Math.round(Math.max(11, slot * 0.14))}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
        c.fillText(j.done ? '✓' : '!', hx + hw / 2, hy - hh * 0.85 + 1);
      } else {
        c.fillStyle = '#2a2a2e'; c.font = `800 ${Math.round(Math.max(10, slot * 0.12))}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
        c.fillText(`${nb.unlockStars}★`, hx + hw / 2, hy - hh * 0.85);
      }
      const fs = Math.round(Math.max(10, slot * 0.13));
      c.font = `800 ${fs}px "Baloo 2", Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
      const tw = c.measureText(nb.name).width + 10, ny = H * 0.74 - fs * 0.35;
      c.fillStyle = 'rgba(247,243,234,.92)'; roundRect(c, hx + hw / 2 - tw / 2, ny - fs * 0.7, tw, fs * 1.4, fs * 0.7); c.fill();
      c.fillStyle = '#3b2a1e'; c.fillText(nb.name, hx + hw / 2, ny + 1);
    });
    // her, arriving from the left along the road
    const ax = W * 0.1, ay = H * 0.97;
    drawSprite(c, s.avatar, ax, ay, Math.max(1.6, W / 250), 0, 'right');
    const pet = activePet(s);
    if (pet) drawPet(c, pet, ax - W * 0.07, ay, Math.max(1.6, W / 250));
  }

  function render() {
    jobs = jobsForToday();
    clear(list);
    for (const j of jobs) {
      const g = GAME_BY_ID[j.job.gameId];
      const borrowed = g && !lockInfo(g).unlocked;
      list.append(h('div.card' + (j.locked ? '.locked' : ''), { style: { borderLeft: `8px solid ${j.neighbor.roof}` } },
        h('div.row', null,
          h('div', { style: { flex: '1' } },
            h('h3', null, j.locked ? `${j.neighbor.name}'s house` : j.job.title),
            h('p.muted.small', null, j.locked ? `Earn ${j.neighbor.unlockStars} stars and ${j.neighbor.name}, ${j.neighbor.role}, will move in.` : `${j.neighbor.name}, ${j.neighbor.role}. ${g ? g.title : ''}.${borrowed && !j.locked ? ` ${j.neighbor.name} lends you this game for today.` : ''}`),
          ),
          j.locked ? null : j.done
            ? h('span.small', { style: { fontWeight: '800', color: 'var(--good)' } }, 'Done today')
            : h('button.btn.small.primary', { onclick: () => startJob(j) }, `+${j.job.bonus} `, h('span.dot.gem', { style: { display: 'inline-block' } })),
        ),
      ));
    }
    const total = Object.values(getSave().town.completed).reduce((a, b) => a + b, 0);
    list.append(h('p.center.muted.small', null, total ? `Jobs done so far: ${total}. Neighbors give thank-you gifts after your 1st, 4th and 8th job for them.` : 'Finish a job and the neighbor may send a gift for your house.'));
    draw();
  }

  render();
  const un = subscribe(render);
  const unArt = onArtReady(draw);
  return () => { un(); unArt(); unPointer(); S.destroy(); };
}
